import React, { useEffect, useState, useRef } from 'react';
import { adalApiFetch } from './adalConfig';
import { LocalizationProvider, StaticDatePicker, PickersDay } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import itLocale from 'date-fns/locale/it';
import { format, isValid } from 'date-fns';
import { Box, CircularProgress, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, MenuItem, Select, List, ListItem, ListItemText, IconButton, Tooltip as MuiTooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

/**
 * CalendarioDisponibilita
 * - vista mensile che evidenzia le date disponibili per prenotazioni
 * - recupera le date da: process.env.REACT_APP_BASE_SERVER_URI + '/visits/available-dates'
 */
const CalendarioDisponibilita = () => {
  // start without a preselected day
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  // availableMap: { 'yyyy-MM-dd' => [ { id, label } ] } for availability dates and rooms
  const [availableMap, setAvailableMap] = useState(new Map());
  // slotMap: { 'yyyy-MM-dd' => [ { id_ambulatorio, ora, ... } ] } for slot availability from /visits/availableslots
  const [slotMap, setSlotMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState('');

  // mounted ref to avoid state updates after unmount
  const mounted = useRef(true);

  const enrichMapWithRooms = (sourceMap, parsedRooms) => {
    const newMap = new Map(sourceMap);
    for (const [day, arr] of newMap.entries()) {
      const updated = arr.map(entry => {
        if (entry && entry.id_ambulatorio != null) {
          const found = parsedRooms.find(p => p.id === String(entry.id_ambulatorio));
          return { ...entry, label: found ? found.label : (entry.label || `Ambulatorio ${entry.id_ambulatorio}`) };
        }
        return entry;
      });
      newMap.set(day, updated);
    }
    return newMap;
  };

  const parseAvailabilityData = (data) => {
    const map = new Map();
    if (!Array.isArray(data)) return map;

    data.forEach(item => {
      if (!item) return;

      if (item.id_disponibilita || item.id_ambulatorio || item.data_inizio || item.data) {
        const start = item.data_inizio ? new Date(item.data_inizio) : (item.data ? new Date(item.data) : null);
        const end = item.data_fine ? new Date(item.data_fine) : start;
        if (!start || !isValid(start)) return;
        const ed = end && isValid(end) ? end : start;

        const cur = new Date(start);
        while (cur <= ed) {
          const dayStr = format(cur, 'yyyy-MM-dd');
          const rawAmbId = item.id_ambulatorio ?? item.id ?? null;
          const entry = { id_disponibilita: item.id_disponibilita ?? null, id_ambulatorio: rawAmbId != null ? String(rawAmbId) : null };
          const existing = map.get(dayStr) || [];
          existing.push(entry);
          map.set(dayStr, existing);
          cur.setDate(cur.getDate() + 1);
        }
        return;
      }

      if (typeof item === 'string') {
        const dt = new Date(item);
        if (!isValid(dt)) return;
        map.set(format(dt, 'yyyy-MM-dd'), [{ id: null, label: 'Disponibile' }]);
        return;
      }

      const dateVal = item.data || item.date || item.giorno || item.day;
      const roomsVal = item.ambulatori || item.rooms || item.ambulatorio || item.items || item.availability;
      const dv = dateVal ? new Date(dateVal) : null;
      if (!dv || !isValid(dv)) return;
      const dateStr = format(dv, 'yyyy-MM-dd');

      let parsedRooms = [];
      if (Array.isArray(roomsVal) && roomsVal.length > 0) {
        parsedRooms = roomsVal.map(r => {
          if (typeof r === 'string' || typeof r === 'number') return { id: String(r), id_disponibilita: null };
          const rawId = r.id || r.id_ambulatorio || r.codice || r.key || null;
          return {
            id: rawId != null ? String(rawId) : null,
            id_disponibilita: r.id_disponibilita || r.idDisponibilita || r.id_disponib || null
          };
        });
      } else if (item.count || item.count_rooms || item.rooms_count) {
        const cnt = item.count || item.count_rooms || item.rooms_count;
        for (let i = 0; i < cnt; i++) parsedRooms.push({ id: null, id_disponibilita: null });
      } else {
        parsedRooms = [{ id: null, id_disponibilita: null }];
      }

      const entries = parsedRooms.map(p => ({ id_disponibilita: p.id_disponibilita ?? null, id_ambulatorio: p.id != null ? String(p.id) : null }));
      map.set(dateStr, entries);
    });

    return map;
  };

  const parseSlotData = (data) => {
    const map = new Map();
    if (!Array.isArray(data)) return map;

    data.forEach(item => {
      if (!item) return;

      const dateVal = item.giorno || item.data_visita || item.data || item.date || item.day;
      const timeVal = item.ora || item.ora_visita || item.time || item.slot || null;
      const rawAmbId = item.id_ambulatorio ?? item.id ?? item.ambulatorio ?? null;

      const dv = dateVal ? new Date(dateVal) : null;
      if (!dv || !isValid(dv)) return;
      const dateStr = format(dv, 'yyyy-MM-dd');

      const entry = {
        id_disponibilita: item.id_disponibilita ?? null,
        id_ambulatorio: rawAmbId != null ? String(rawAmbId) : null,
        ora: timeVal != null ? String(timeVal) : null
      };

      const existing = map.get(dateStr) || [];
      existing.push(entry);
      map.set(dateStr, existing);
    });

    return map;
  };

  const parseRoomsData = (data) => {
    if (!Array.isArray(data) || data.length === 0) return [];
    return data.map(x => {
      const rawId = x.id_ambulatorio ?? x.id ?? x.key ?? x.codice ?? x.value ?? null;
      return { id: rawId != null ? String(rawId) : null, label: x.descrizione ?? x.nome ?? x.label ?? x.text ?? String(x) };
    });
  };

  const loadAvailabilityData = async () => {
    if (!mounted.current) return;

    setLoading(true);
    setError(null);

    try {
      const todayStr = format(new Date(), 'yyyy-MM-dd');
      const availabilityUrl = process.env.REACT_APP_BASE_SERVER_URI + '/visits/disponibilita?from=' + todayStr + '&disponibilita=1';
      const slotsUrl = process.env.REACT_APP_BASE_SERVER_URI + '/visits/availableslots?from=' + todayStr;
      const roomsUrl = process.env.REACT_APP_BASE_SERVER_URI + '/visits/rooms';

      const [availabilityRes, slotsRes, roomsRes] = await Promise.all([
        adalApiFetch(fetch, availabilityUrl),
        adalApiFetch(fetch, slotsUrl),
        adalApiFetch(fetch, roomsUrl)
      ]);

      if (!availabilityRes.ok) throw new Error('no-data');
      if (!slotsRes.ok) throw new Error('no-slots');
      if (!roomsRes.ok) throw new Error('no-rooms');

      const [availabilityData, slotsData, roomsData] = await Promise.all([
        availabilityRes.json(),
        slotsRes.json(),
        roomsRes.json()
      ]);

      const parsedAvailability = parseAvailabilityData(availabilityData);
      const parsedSlots = parseSlotData(slotsData);
      const parsedRooms = parseRoomsData(roomsData);

      if (mounted.current) {
        if (parsedRooms.length > 0) {
          setRooms(parsedRooms);
          setSelectedRoomId(prev => prev || parsedRooms[0].id);
          setAvailableMap(enrichMapWithRooms(parsedAvailability, parsedRooms));
          setSlotMap(enrichMapWithRooms(parsedSlots, parsedRooms));
        } else {
          setRooms([]);
          setAvailableMap(parsedAvailability);
          setSlotMap(parsedSlots);
        }
      }
    } catch (err) {
      if (mounted.current) {
        setError(err && err.message ? err.message : 'Errore nel caricamento delle disponibilità');
      }
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadAvailabilityData();
    return () => { mounted.current = false };
  }, []);

  // helper: get rooms entries for a given day
  const getRoomsForDate = (day) => {
    if (!day || !isValid(day)) return [];
    const key = format(day, 'yyyy-MM-dd');
    return availableMap.get(key) || [];
  };

  const getAvailableRoomsForSelectedDate = () => {
    const currentEntries = getRoomsForDate(selectedDate);
    const usedRoomIds = new Set(
      currentEntries
        .map(entry => entry?.id_ambulatorio)
        .filter(id => id != null && id !== '')
        .map(id => String(id))
    );

    return rooms.filter(room => !usedRoomIds.has(String(room.id)));
  };

  useEffect(() => {
    if (!dialogOpen) return;

    const availableRooms = getAvailableRoomsForSelectedDate();
    if (availableRooms.length === 0) {
      setSelectedRoomId('');
      return;
    }

    if (!availableRooms.some(room => String(room.id) === String(selectedRoomId))) {
      setSelectedRoomId(availableRooms[0].id);
    }
  }, [dialogOpen, selectedDate, availableMap, rooms, selectedRoomId]);

  const getSlotColor = (count) => {
    if (count > 9) return 'green';
    if (count >= 4) return 'goldenrod';
    return 'red';
  };

  const getUpcomingSlots = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return Array.from(slotMap.entries())
      .map(([dateStr, entries]) => {
        const day = new Date(`${dateStr}T00:00:00`);
        const validEntries = (entries || []).filter(e => e && (e.id_ambulatorio != null || e.id_disponibilita != null));
        return { dateStr, day, slotCount: validEntries.length };
      })
      .filter(item => item.day >= today && item.slotCount > 0)
      .sort((a, b) => a.day - b.day)
      .slice(0, 8);
  };

  // coloring: deterministic color per room id and a distinct color for multi-room days
  // softer / pastel palette for ambulatori badges
  const roomColorPalette = ['#AEDFF7', '#F7C8D9', '#C8E6C9', '#FFE0B2', '#D1C4E9', '#FFF59D', '#B2EBF2', '#D7CCC8'];
  const multiRoomColor = '#e4d61aff'; 

  const hashString = (s) => {
    if (!s) return 0;
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i);
    return Math.abs(h);
  };

  const getRoomColor = (roomId) => {
    if (!roomId) return '#ECEFF1';
    const idx = hashString(String(roomId)) % roomColorPalette.length;
    return roomColorPalette[idx];
  };

  // simple luminance check to decide text color on badge
  const isDark = (hex) => {
    if (!hex) return false;
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    // relative luminance
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return lum < 140;
  };

  // Holidays: compute Italian national holidays (including Easter and Easter Monday)
  const easterDate = (Y) => {
    // Anonymous Gregorian algorithm
    const a = Y % 19;
    const b = Math.floor(Y / 100);
    const c = Y % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(Y, month - 1, day);
  };

  const getItalianHolidays = (year) => {
    const s = new Set();
    const add = (m, d) => s.add(format(new Date(year, m - 1, d), 'yyyy-MM-dd'));
    // fixed-date national holidays
    add(1, 1); // Capodanno
    add(1, 6); // Epifania
    add(4, 25); // Festa della Liberazione
    add(5, 1); // Festa del Lavoro
    add(6, 2); // Festa della Repubblica
    add(8, 15); // Ferragosto
    add(11, 1); // Ognissanti
    add(12, 8); // Immacolata Concezione
    add(12, 25); // Natale
    add(12, 26); // Santo Stefano
    // Easter and Easter Monday
    const e = easterDate(year);
    s.add(format(e, 'yyyy-MM-dd'));
    const em = new Date(e);
    em.setDate(em.getDate() + 1);
    s.add(format(em, 'yyyy-MM-dd'));
    return s;
  };

  const shouldDisableDate = (day) => {
    if (!day || !isValid(day)) return false;
    const dow = day.getDay();
    // Sunday (0) and Saturday (6)
    if (dow === 0 || dow === 6) return true;
    const holidays = getItalianHolidays(day.getFullYear());
    const key = format(day, 'yyyy-MM-dd');
    return holidays.has(key);
  };

  const DayWithBadge = (props) => {
    const { day } = props;
    const key = format(day, 'yyyy-MM-dd');
    const arr = availableMap.get(key) || [];
    const validEntries = arr.filter(e => e && (e.id_ambulatorio != null || e.id_disponibilita != null));
    const count = validEntries.length;

    // badge color decision
    let badgeColor = null;
    let badgeTextColor = '#000';
    if (count === 1) {
      badgeColor = getRoomColor(validEntries[0].id_ambulatorio);
      badgeTextColor = isDark(badgeColor) ? '#fff' : '#000';
    } else if (count > 1) {
      badgeColor = multiRoomColor;
      badgeTextColor = isDark(badgeColor) ? '#fff' : '#000';
    }

    return (
      <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <PickersDay {...props} sx={{ fontSize: '1.6rem', fontWeight: 400, zIndex: 1 }} />
        {count > 0 && (
          <Box aria-hidden sx={{
            minWidth: 12,
            height: 12,
            borderRadius: '12px',
            backgroundColor: badgeColor,
            color: badgeTextColor,
            fontSize: '0.6rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            bottom: 4,
            left: '72%',
            transform: 'translateX(-10%)',
            px: 0.4,
            lineHeight: 1
          }}></Box>
        )}
      </Box>
    );
  };
  return (
    <>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 2 }}>
          <Typography color="error" gutterBottom>Si è verificato un errore: {error}</Typography>
          <Button variant="contained" onClick={() => {
            setLoading(true);
            setError(null);
            // trigger a re-run of the effect by resetting state: simplest is to reload the page
            window.location.reload();
          }}>Riprova</Button>
        </Box>
      ) : (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={itLocale}>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: { xs: 2, md: 3 },
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <Box sx={{ flex: '0 1 320px', minWidth: 0, display: 'flex', justifyContent: 'flex-start' }}>
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                openTo="day"
                value={selectedDate}
                referenceDate={calendarMonth}
                onMonthChange={setCalendarMonth}
                onChange={(newValue) => {
                  // prevent selecting disabled dates (weekends/holidays)
                  if (!newValue) return;
                  if (shouldDisableDate(newValue)) return;
                  setSelectedDate(newValue);
                  setDialogOpen(true);
                }}
                // provide a Day slot component to ensure custom rendering in newer MUI X versions
                slots={{ day: DayWithBadge }}
                shouldDisableDate={shouldDisableDate}
                sx={{
                  width: '100%',
                  maxWidth: 360,
                  '& .MuiPickersLayout-root': { width: '100%' },
                  '& .MuiDateCalendar-root': { width: '100%', maxWidth: 320 },
                  '& .MuiPickersCalendarHeader-root': { px: 1 },
                  '& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer': { justifyContent: 'space-around' },
                  '& .MuiPickersDay-root': { mx: 0 }
                }}
              />
            </Box>

            {/* Legend on the right */}
            <Box sx={{ flex: '0 1 220px', minWidth: 0, maxWidth: 320 }}>
              <Typography variant="subtitle1">Legenda</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 18, height: 18, borderRadius: 1, backgroundColor: multiRoomColor }} />
                  <Typography variant="body2">Più ambulatori disponibili</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 18, height: 18, borderRadius: 1, backgroundColor: '#ECEFF1' }} />
                  <Typography variant="body2">Giorni non selezionabili (weekend / festività)</Typography>
                </Box>

                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2">Ambulatori</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5, maxHeight: 220, overflow: 'auto' }}>
                    {rooms && rooms.length > 0 ? rooms.map(room => (
                      <Box key={room.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Box sx={{ width: 14, height: 14, borderRadius: 1, backgroundColor: getRoomColor(room.id) }} />
                        <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{room.label}</Typography>
                      </Box>
                    )) : (
                      <Typography variant="body2" color="text.secondary">Nessun ambulatorio caricato</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ flex: '0 1 260px', minWidth: 0, maxWidth: 360 }}>
              <Typography variant="subtitle1">Slot disponibili</Typography>
              <List dense sx={{ mt: 1, maxHeight: 500, overflow: 'auto', minWidth: 0 }}>
                {getUpcomingSlots().length > 0 ? getUpcomingSlots().map(item => (
                  <ListItem key={item.dateStr} sx={{ borderRadius: 1 }}>
                    <ListItemText
                      primary={format(item.day, 'dd/MM/yyyy', { locale: itLocale })}
                      primaryTypographyProps={{ sx: { color: getSlotColor(item.slotCount), fontWeight: 700 } }}
                      secondary={`${item.slotCount} slot disponibil${item.slotCount === 1 ? 'e' : 'i'}`}
                    />
                  </ListItem>
                )) : (
                  <Typography variant="body2" color="text.secondary">Nessuno slot futuro disponibile</Typography>
                )}
              </List>
            </Box>
          </Box>
        </LocalizationProvider>
      )}

      {/* Dialog per gestire disponibilità per la data selezionata */}
  <Dialog fullWidth maxWidth="sm" open={dialogOpen} onClose={() => { setDialogOpen(false); setSelectedDate(null); }}>
    <DialogTitle>Disponibilità per il {selectedDate && isValid(selectedDate) ? format(selectedDate, 'dd/MM/yyyy') : ''}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1">Disponibili</Typography>
          <List>
            {getRoomsForDate(selectedDate).map((r, idx) => (
              <ListItem key={idx} secondaryAction={
                <IconButton edge="end" aria-label="delete" onClick={async () => {
                  // attempt to delete availability by id_disponibilita
                  try {
                    const dateStr = format(selectedDate, 'yyyy-MM-dd');
                    const idToSend = r.id_disponibilita;
                    if (!idToSend) {
                      // nothing to send - cannot delete a disponibilita without id
                      alert('Impossibile cancellare: disponibilità senza id.');
                      return;
                    }
                    const resp = await adalApiFetch(fetch, process.env.REACT_APP_BASE_SERVER_URI + '/visits/deldisponibilita', {
                      method: 'post',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ id_disponibilita: idToSend })
                    });
                    if (!resp.ok) throw new Error('delete-failed');
                    await loadAvailabilityData();
                    setDialogOpen(false);
                    setSelectedDate(null);
                  } catch (e) {
                    alert('Errore durante la cancellazione della disponibilità.');
                  }
                }}>
                  <DeleteIcon />
                </IconButton>
              }>
                <ListItemText primary={(() => {
                  const roomLabel = rooms.find(room => room.id === r.id_ambulatorio)?.label;
                  return roomLabel || (r.id_ambulatorio ? `Ambulatorio ${r.id_ambulatorio}` : 'Disponibile');
                })()} />
              </ListItem>
            ))}
          </List>

          <Typography variant="subtitle1" sx={{ mt: 2 }}>Aggiungi</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 1 }}>
            {getAvailableRoomsForSelectedDate().length > 0 ? (
              <>
                <Select value={selectedRoomId} onChange={(e) => setSelectedRoomId(e.target.value)} sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                  {getAvailableRoomsForSelectedDate().map(room => (
                    <MenuItem key={room.id} value={room.id}>{room.label}</MenuItem>
                  ))}
                </Select>
                <Button variant="contained" startIcon={<AddIcon />} disabled={!selectedRoomId} onClick={async () => {
                  try {
                    const dateStr = format(selectedDate, 'yyyy-MM-dd');
                    const resp = await adalApiFetch(fetch, process.env.REACT_APP_BASE_SERVER_URI + '/visits/insdisponibilita', {
                      method: 'post',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ disponibilita: { data_inizio: dateStr, data_fine: dateStr, id_ambulatorio: selectedRoomId, disponibilita: 1 } })
                    });
                    if (!resp.ok) throw new Error('add-failed');
                    await loadAvailabilityData();
                    setDialogOpen(false);
                    setSelectedDate(null);
                  } catch (e) {
                    alert('Errore durante l\'aggiunta della disponibilità.');
                  }
                }}>Aggiungi</Button>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">Tutti gli ambulatori per questo giorno sono già presenti.</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setSelectedDate(null); }}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CalendarioDisponibilita;
